import "urlpattern-polyfill";
import "@oddbird/popover-polyfill";
import "./zodJitless";
import * as Sentry from "@sentry/react";
import "./global.css";
import { setup as gooberSetup } from "goober";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import tinesLogo from "./assets/images/tines-logo.svg";
import tinesLogoDark from "./assets/images/tines-logo-dark.svg";
import tinesPurple from "./assets/images/tines-purple.svg";
import tinesRed from "./assets/images/tines-red.svg";
import tinesYellow from "./assets/images/tines-yellow.svg";
import { App } from "./components/App";
import type { Favicon } from "./environment";
import { config } from "./environment";
import { installBrowserNavigation } from "./lib/urls";

type BrowserNetworkInformation = {
  effectiveType?: string;
  rtt?: number;
};

function getBrowserDebugContext() {
  const network = (
    navigator as Navigator & { connection?: BrowserNetworkInformation }
  ).connection;
  const extra: Record<string, number> = {};
  const tags: Record<string, string> = {
    tab_hidden: String(document.visibilityState === "hidden"),
  };
  if (network?.effectiveType) {
    tags["network.effective_type"] = network.effectiveType;
  }
  if (typeof network?.rtt === "number") extra["network.rtt"] = network.rtt;
  return { extra, tags };
}

function normalizeConsoleMessage(message: string) {
  return message
    .replace(/\s*\{[\s\S]*$/, "")
    .replace(/clientID=\S+/g, "")
    .replace(/wsid=\S+/g, "")
    .replace(/runLoopCounter=\d+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

if (config.sentryDsn) {
  Sentry.init({
    dsn: config.sentryDsn,
    release: config.gitSha,
    sendDefaultPii: true,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
      // Drops errors thrown by code that isn't ours — DevTools console
      // snippets, extensions, injected scripts. The vite plugin stamps our
      // chunks with the matching applicationKey.
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["3b-ui"],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
    ],
    initialScope: {
      tags: { stack: config.stackName },
    },
    beforeSend: (event) => {
      const browserDebugContext = getBrowserDebugContext();
      event.extra = {
        ...event.extra,
        ...browserDebugContext.extra,
      };
      event.tags = {
        ...event.tags,
        ...browserDebugContext.tags,
      };
      if (event.logger === "console" && event.message) {
        const core = normalizeConsoleMessage(event.message);
        event.fingerprint = [core];
        event.message = core;
      }
      return event;
    },
  });
}

gooberSetup(createElement);
installBrowserNavigation();

const defaultFavicons = {
  light: tinesLogo,
  dark: tinesLogoDark,
} as const;

const overrideFavicons: Record<Favicon, string> = {
  "logo-red.svg": tinesRed,
  "logo-yellow.svg": tinesYellow,
  "logo-purple.svg": tinesPurple,
};

// Set favicon based on environment
const links = document.querySelectorAll('link[rel="icon"]');
links.forEach((link) => {
  const faviconLink = link as HTMLLinkElement;
  if (config.favicon) {
    faviconLink.href = overrideFavicons[config.favicon];
    faviconLink.removeAttribute("media");
    return;
  }

  if (faviconLink.media.includes("dark")) {
    faviconLink.href = defaultFavicons.dark;
    return;
  }

  faviconLink.href = defaultFavicons.light;
});

const container = document.getElementById("root");
if (container) {
  // Sentry.reactErrorHandler doesn't touch the console, so without a DSN
  // a render-time throw leaves the page silently blank.
  const reactErrorHandler = Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("[react.render]", error, errorInfo);
  });

  createRoot(container, {
    onUncaughtError: reactErrorHandler,
    onCaughtError: reactErrorHandler,
    onRecoverableError: reactErrorHandler,
  }).render(<App />);
}
